import { storeToRefs } from 'pinia'
import { computed, ref, watch, type ComputedRef, type Ref } from 'vue'

import { useAuthStore } from '@/auth/useAuthStore'
import { type HestiaIframeExtraData } from '@/features/hestia/composables/UseHestiaIframe'
import {
  useFeatureFlags,
  useReactiveFeatureFlag,
} from '@/utils/useFeatureFlags'

export type WebsiteBuilderDesignMode = 'legacy' | 'variantB'
export type WebsiteBuilderDesignModePreference =
  | 'default'
  | WebsiteBuilderDesignMode

interface UseWebsiteBuilderDesignModePreferenceOptions {
  brandId: Ref<string | undefined>
  variantBTargetPath: Ref<string | undefined> | string
}

interface DesignModeOption {
  label: string
  value: WebsiteBuilderDesignModePreference
}

interface UseWebsiteBuilderDesignModePreferenceResult {
  designModeExtraData: ComputedRef<HestiaIframeExtraData | undefined>
  designModeOptions: DesignModeOption[]
  designModePreference: Ref<WebsiteBuilderDesignModePreference>
  designModeTargetPath: ComputedRef<string | undefined>
  showDesignModeSelector: ComputedRef<boolean>
}

const STORAGE_PREFIX = 'owner.websiteBuilderDesignMode'
const MODE_SWITCH_CONFIRMATION =
  'Switching website builder design modes reloads the editor. Continue?'
export const WEBSITE_BUILDER_DESIGN_MODE_SELECTOR_FLAG =
  'hermes.websiteBuilderDesignModeSelector'

export const WEBSITE_BUILDER_DESIGN_MODE_OPTIONS: DesignModeOption[] = [
  { label: 'Flag default', value: 'default' },
  { label: 'Legacy', value: 'legacy' },
  { label: 'New', value: 'variantB' },
]

/**
 * Narrows persisted values before they can affect iframe routing.
 * @param value
 */
export function isWebsiteBuilderDesignModePreference(
  value: string | null
): value is WebsiteBuilderDesignModePreference {
  return value === 'default' || value === 'legacy' || value === 'variantB'
}

/**
 * Scopes admin overrides by brand so beta checks do not bleed across brands.
 * @param brandId
 */
export function getWebsiteBuilderDesignModeStorageKey(brandId: string): string {
  return `${STORAGE_PREFIX}:${brandId}`
}

/**
 * Reads a stored override, falling back to Unleash for stale or invalid data.
 * @param brandId
 */
export function getStoredWebsiteBuilderDesignModePreference(
  brandId: string
): WebsiteBuilderDesignModePreference {
  if (typeof window === 'undefined') {
    return 'default'
  }

  const value = window.localStorage.getItem(
    getWebsiteBuilderDesignModeStorageKey(brandId)
  )

  return isWebsiteBuilderDesignModePreference(value) ? value : 'default'
}

/**
 * Persists only explicit overrides; default mode removes the local key.
 * @param brandId
 * @param preference
 */
export function setStoredWebsiteBuilderDesignModePreference(
  brandId: string,
  preference: WebsiteBuilderDesignModePreference
): void {
  if (typeof window === 'undefined') {
    return
  }

  const key = getWebsiteBuilderDesignModeStorageKey(brandId)
  if (preference === 'default') {
    window.localStorage.removeItem(key)
  } else {
    window.localStorage.setItem(key, preference)
  }
}

/** Warns before remounting the iframe, which can discard unsaved Hestia edits. */
function confirmDesignModeSwitch(): boolean {
  if (typeof window === 'undefined' || typeof window.confirm !== 'function') {
    return true
  }

  return window.confirm(MODE_SWITCH_CONFIRMATION)
}

/**
 * Unwraps Vue refs at the Hermes/Hestia session boundary.
 * @param targetPath
 */
function resolveTargetPath(
  targetPath: Ref<string | undefined> | string
): string | undefined {
  return typeof targetPath === 'string' ? targetPath : targetPath.value
}

/**
 * Resolves the target path and iframe payload for the active design mode.
 * @param root0
 * @param root0.brandId
 * @param root0.variantBTargetPath
 */
export function useWebsiteBuilderDesignModePreference({
  brandId,
  variantBTargetPath,
}: UseWebsiteBuilderDesignModePreferenceOptions): UseWebsiteBuilderDesignModePreferenceResult {
  const { isSuperAdmin } = storeToRefs(useAuthStore())
  const { enabled: variantBRoutesEnabled } = useReactiveFeatureFlag(
    'hermes.websiteVariantBRoutes'
  )
  const featureFlags = useFeatureFlags()
  const designModeSelectorEnabledAtMount = featureFlags.isEnabled(
    WEBSITE_BUILDER_DESIGN_MODE_SELECTOR_FLAG
  )
  const storedPreference = ref<WebsiteBuilderDesignModePreference>('default')

  watch(
    brandId,
    (nextBrandId) => {
      storedPreference.value = nextBrandId
        ? getStoredWebsiteBuilderDesignModePreference(nextBrandId)
        : 'default'
    },
    { immediate: true }
  )

  const canUseDesignModeSelector = computed(
    () => isSuperAdmin.value && designModeSelectorEnabledAtMount
  )

  const designModePreference = computed<WebsiteBuilderDesignModePreference>({
    get: () =>
      canUseDesignModeSelector.value ? storedPreference.value : 'default',
    set: (nextPreference) => {
      if (
        !canUseDesignModeSelector.value ||
        storedPreference.value === nextPreference
      ) {
        return
      }

      if (!confirmDesignModeSwitch()) {
        return
      }

      storedPreference.value = nextPreference
      if (brandId.value) {
        setStoredWebsiteBuilderDesignModePreference(
          brandId.value,
          nextPreference
        )
      }
    },
  })

  const defaultMode = computed<WebsiteBuilderDesignMode>(() =>
    variantBRoutesEnabled.value ? 'variantB' : 'legacy'
  )
  const effectiveMode = computed<WebsiteBuilderDesignMode>(() =>
    designModePreference.value === 'default'
      ? defaultMode.value
      : designModePreference.value
  )

  return {
    designModeExtraData: computed(() =>
      canUseDesignModeSelector.value && designModePreference.value !== 'default'
        ? {
            websiteBuilder: {
              designMode: designModePreference.value,
            },
          }
        : undefined
    ),
    designModeOptions: WEBSITE_BUILDER_DESIGN_MODE_OPTIONS,
    designModePreference,
    designModeTargetPath: computed(() =>
      effectiveMode.value === 'variantB'
        ? resolveTargetPath(variantBTargetPath)
        : undefined
    ),
    showDesignModeSelector: canUseDesignModeSelector,
  }
}
